"use client";

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import { services } from "data/services";
import { Search, ChevronRight } from "lucide-react";

const ServiceCards = ({ className = "" }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleCardClick = (id) => {
    if (!isHydrated) return;
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <section className={`py-4 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-4 space-y-2 sm:space-y-8 ">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-2">
            Creative Artistry <span className="text-accent">That Works</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground">
            Comprehensive creative services engineered for maximum business
            impact
          </p>
        </div>

        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 6).map((service) => (
            <div
              key={service.id}
              className={`bg-card  border rounded-lg overflow-hidden transition-all duration-300 hover:border-red-600 hover:shadow-dramatic shadow-subtle cursor-pointer ${
                isHydrated && expandedCard === service.id ? "lg:col-span-2" : ""
              }`}
              onClick={() => handleCardClick(service.id)}
            >
              <div className="p-4 flex flex-col h-full">
                {/* HEADER SLOT — SAME START POINT */}
                <div className="min-h-[36px] flex items-start">
                  <h3 className=" text-xl sm:text-2xl font-headline font-bold text-red-600 leading-tight">
                    {service.title}
                  </h3>
                </div>

                {/* BODY */}
                <div className=" space-y-4">
                  <p className="text-muted-foreground leading-relaxed">
                    {service.shortdescription}
                  </p>

                  {isHydrated && expandedCard === service.id && (
                    <div className="pt-2 space-y-3 animate-fade-in">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-success flex-shrink-0 mt-1"
                          />

                          <span className="text-sm text-foreground">
                            <strong>{feature.title}</strong> —{" "}
                            {feature.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="mt-auto pt-6">
                  <Link
                    to={service.link}
                    className="inline-flex items-center font-body text-sm font-medium text-white border-border  border-red-600 hover:text-black  transition-colors duration-300 bg-red-600 hover:border-red-700 hover:bg-white px-3 py-1 sm:py-2 rounded-md"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>Learn More</span>
                    <Icon
                      name="ArrowRightIcon"
                      size={20}
                      className="transform group-hover:translate-x-1 transition-transform duration-300"
                    />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 mb-2 flex justify-center">
          <Link to="/corporate-solutions-services">
            <button className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-red-50 to-red-50/50 border border-red-200 text-sm sm:text-xl font-semibold text-gray-900 transition-all duration-300 active:scale-95 lg:hover:scale-105 lg:hover:shadow-xl lg:hover:shadow-red-900/10">
              <Search className="w-4 sm:w-5 h-4 sm:h-5 text-red-600 lg:group-hover:rotate-12 transition-transform" />

              <span>Find out more about our services</span>

              <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-red-600 lg:group-hover:translate-x-2 transition-transform" />
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceCards;
